// Round-trip URL search params ↔ typed filter objects for
// `/tasks-management/{tasks,collections}`. Used by the routes'
// `validateSearch` and consumed via `useSearch` in the widgets.
//
// Modeled on `apps/products/web/src/features/products-filters/model/
// search-params.ts`. Same coercion + filter-out-bad-values discipline.

import type {
  TaskFlag,
  TaskPriority,
  TaskProfile,
} from '@travel-planner/shared/schemas/tasks';

export type TasksManagementSearch = {
  title?: string;
  priorities?: TaskPriority[];
  profiles?: TaskProfile[];
  flags?: TaskFlag[];
  itineraryIds?: string[];
  collectionIds?: string[];
  assignedAccountId?: string;
  completed?: boolean;
  sortBy?:
    | 'title'
    | 'priority'
    | 'profile'
    | 'itinerary'
    | 'collection'
    | 'expireOn'
    | 'completedOn'
    | 'createdOn';
  sortOrder?: 'asc' | 'desc';
  page?: number;
};

export type CollectionsSearch = {
  title?: string;
  page?: number;
};

const TASK_PRIORITY_VALUES: ReadonlyArray<TaskPriority> = [
  'high',
  'medium',
  'low',
];

const TASK_PROFILE_VALUES: ReadonlyArray<TaskProfile> = [
  'consultant',
  'finance',
  'operation',
];

const TASK_FLAG_VALUES: ReadonlyArray<TaskFlag> = [0, 5, 10];

const SORT_BY_VALUES: ReadonlyArray<
  NonNullable<TasksManagementSearch['sortBy']>
> = [
  'title',
  'priority',
  'profile',
  'itinerary',
  'collection',
  'expireOn',
  'completedOn',
  'createdOn',
];

const asStringArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asPriorityArray = (v: unknown): TaskPriority[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const filtered = v.filter((x): x is TaskPriority =>
    (TASK_PRIORITY_VALUES as readonly string[]).includes(x as string)
  );
  return filtered.length > 0 ? filtered : undefined;
};

const asProfileArray = (v: unknown): TaskProfile[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const filtered = v.filter((x): x is TaskProfile =>
    (TASK_PROFILE_VALUES as readonly string[]).includes(x as string)
  );
  return filtered.length > 0 ? filtered : undefined;
};

const asFlagArray = (v: unknown): TaskFlag[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const filtered = v.filter((x): x is TaskFlag =>
    (TASK_FLAG_VALUES as readonly number[]).includes(x as number)
  );
  return filtered.length > 0 ? filtered : undefined;
};

const asSortBy = (v: unknown): TasksManagementSearch['sortBy'] =>
  typeof v === 'string' && (SORT_BY_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as TasksManagementSearch['sortBy'])
    : undefined;

const asSortOrder = (v: unknown): TasksManagementSearch['sortOrder'] =>
  v === 'asc' || v === 'desc' ? v : undefined;

const asBoolean = (v: unknown): boolean | undefined => {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
};

const asPositiveInteger = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isInteger(v) && v > 0) return v;
  if (typeof v === 'string') {
    const parsed = Number.parseInt(v, 10);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
  }
  return undefined;
};

export const validateTasksManagementSearch = (
  input: Record<string, unknown>
): TasksManagementSearch => ({
  title: typeof input.title === 'string' ? input.title : undefined,
  priorities: asPriorityArray(input.priorities),
  profiles: asProfileArray(input.profiles),
  flags: asFlagArray(input.flags),
  itineraryIds: asStringArray(input.itineraryIds),
  collectionIds: asStringArray(input.collectionIds),
  assignedAccountId:
    typeof input.assignedAccountId === 'string'
      ? input.assignedAccountId
      : undefined,
  completed: asBoolean(input.completed),
  sortBy: asSortBy(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
  page: asPositiveInteger(input.page),
});

export const validateCollectionsSearch = (
  input: Record<string, unknown>
): CollectionsSearch => ({
  title: typeof input.title === 'string' ? input.title : undefined,
  page: asPositiveInteger(input.page),
});
